import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  BanknotesIcon,
  ArrowDownTrayIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Portal from '../../components/Portal';
import HelpButton from '../../components/HelpButton';
import { helpContent } from '../../data/helpContent';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY_AMAIR);
const PAGE_SIZE = 10;

const api = (path) => `${process.env.REACT_APP_API_URL}${path}`;

const TRANSACTION_TYPE_LABELS = {
  reservation: 'Réservation',
  paiement: 'Payé',
  annulation: 'Annulation',
  ajustement: 'Ajustement',
};

const PAYMENT_METHOD_LABELS = {
  espece: 'Espèces',
  cheque: 'Chèque',
  stripe: 'Carte bancaire',
  virement: 'Virement',
  solde: 'Avoir (solde)',
};

const transactionTypeLabel = (type) =>
  TRANSACTION_TYPE_LABELS[type] || type;

const formatTransactionDescription = (description) =>
  (description || '').replace(/^Dette miellerie/, 'Dette usage de la miellerie');

const paymentMethodLabel = (methode) =>
  PAYMENT_METHOD_LABELS[methode] || methode;

function formatTransactionQuand(date) {
  if (!date) return '';
  const d = new Date(date);
  const jour = d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const heure = d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${jour} à ${heure}`;
}

function estPrepayementEnLigne(transaction) {
  if (transaction?.paiement?.methode !== 'stripe') return false;
  return /prépay/i.test(transaction.description || '');
}

function estAvoir(transaction) {
  if (!transaction) return false;
  if (transaction.type === 'annulation') return true;
  return transaction.type === 'ajustement' && Number(transaction.montant) > 0;
}

function RegulariserDetteForm({
  dette,
  montantPaiement,
  setMontantPaiement,
  processingPayment,
  onSubmit,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Montant à payer (max: {dette.toFixed(2)} €)
        </label>
        <div className="relative">
          <input
            type="number"
            step="0.01"
            min="0.01"
            max={dette}
            value={montantPaiement}
            onChange={(e) => setMontantPaiement(e.target.value)}
            className="w-full px-3 py-2 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
            placeholder="0.00"
            required
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMontantPaiement(dette.toFixed(2))}
          className="px-3 py-1.5 text-sm bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
        >
          Payer la totalité
        </button>
        <button
          type="button"
          onClick={() => setMontantPaiement((dette / 2).toFixed(2))}
          className="px-3 py-1.5 text-sm bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
        >
          Payer la moitié
        </button>
      </div>

      <button
        type="submit"
        disabled={processingPayment || !montantPaiement}
        className="w-full px-4 py-3 bg-honey-600 text-white rounded-lg hover:bg-honey-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        {processingPayment ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            Redirection vers le paiement...
          </>
        ) : (
          <>
            <CreditCardIcon className="h-5 w-5" />
            Payer par carte bancaire
          </>
        )}
      </button>

      <p className="text-sm text-slate-500 text-center">
        Paiement sécurisé par Stripe
      </p>
    </form>
  );
}

export default function SoldeDette() {
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [montantPaiement, setMontantPaiement] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchBalance();
    checkPaymentStatus();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [transactions.length]);

  const checkPaymentStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      const sessionId = urlParams.get('session_id');
      const token = localStorage.getItem('token');
      if (sessionId && token) {
        try {
          await axios.post(
            api('/miellerie/balance/confirm-session'),
            { sessionId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (err) {
          console.error('Confirmation paiement solde miellerie:', err);
        }
      }
      setSuccess('Paiement effectué avec succès ! Votre solde a été mis à jour.');
      window.history.replaceState({}, '', window.location.pathname);
      fetchBalance();
    } else if (urlParams.get('canceled') === 'true') {
      setError('Paiement annulé.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(api('/miellerie/balance?organisme=AMAIR'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBalance(data.balance);
      setTransactions(data.transactions);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement du solde');
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    const montant = parseFloat(montantPaiement);
    if (!montant || montant <= 0) {
      setError('Veuillez entrer un montant valide');
      return;
    }

    const dette = Math.abs(balance.solde);
    if (montant > dette) {
      setError(`Le montant ne peut pas dépasser votre dette de ${dette.toFixed(2)}€`);
      return;
    }

    setProcessingPayment(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        api('/miellerie/balance/pay'),
        { organisme: 'AMAIR', montant },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const stripe = await stripePromise;
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessingPayment(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création de la session de paiement');
      setProcessingPayment(false);
    }
  };

  const getTransactionIcon = (transaction) => {
    if (estAvoir(transaction)) {
      return <CheckCircleIcon className="h-5 w-5 text-blue-600" />;
    }
    switch (transaction.type) {
      case 'paiement':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'reservation':
        return <ArrowDownTrayIcon className="h-5 w-5 text-red-600" />;
      case 'annulation':
        return <CheckCircleIcon className="h-5 w-5 text-blue-600" />;
      case 'ajustement':
        return <ClockIcon className="h-5 w-5 text-honey-600" />;
      default:
        return <BanknotesIcon className="h-5 w-5 text-slate-600" />;
    }
  };

  const getTransactionColor = (transaction) => {
    if (estAvoir(transaction)) return 'text-blue-600';
    switch (transaction.type) {
      case 'paiement':
        return 'text-green-600';
      case 'reservation':
        return 'text-red-600';
      case 'annulation':
        return 'text-blue-600';
      case 'ajustement':
        return 'text-honey-600';
      default:
        return 'text-slate-600';
    }
  };

  const totalPages = Math.max(1, Math.ceil(transactions.length / PAGE_SIZE));
  const paginatedTransactions = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return transactions.slice(start, start + PAGE_SIZE);
  }, [transactions, page]);

  const closePaymentModal = () => {
    if (processingPayment) return;
    setShowPaymentModal(false);
    setMontantPaiement('');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600" />
      </div>
    );
  }

  const dette = balance && balance.solde < 0 ? Math.abs(balance.solde) : 0;
  const credit = balance && balance.solde > 0 ? balance.solde : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Mon solde miellerie</h2>
          <p className="text-sm text-slate-600 mt-1">
            Dette, crédit et historique de vos usages à la miellerie collective
          </p>
        </div>
        <HelpButton
          title={helpContent.miellerieSolde.title}
          sections={helpContent.miellerieSolde.sections}
        />
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">Erreur</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button type="button" onClick={() => setError('')} className="text-red-600 hover:text-red-800">×</button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-medium">Succès</p>
            <p className="text-green-700 text-sm">{success}</p>
          </div>
          <button type="button" onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">×</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Colonne 1/3 — solde */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-gradient-to-br from-honey-500 to-honey-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-honey-100 text-sm font-medium mb-1">Solde actuel</p>
                <p className="text-4xl font-bold">
                  {balance ? balance.solde.toFixed(2) : '0.00'} €
                </p>
              </div>
              <BanknotesIcon className="h-14 w-14 text-honey-200 opacity-50 flex-shrink-0" />
            </div>

            {dette > 0 && (
              <div className="bg-white bg-opacity-20 rounded-lg p-3 backdrop-blur-sm mb-4">
                <div className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5" />
                  <span className="font-medium">Dette à régulariser : {dette.toFixed(2)} €</span>
                </div>
              </div>
            )}

            {credit > 0 && (
              <div className="bg-white bg-opacity-20 rounded-lg p-3 backdrop-blur-sm mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="font-medium">Crédit disponible</span>
                </div>
                <p className="text-2xl font-bold">{credit.toFixed(2)} €</p>
              </div>
            )}

            {balance && balance.accesBloqueDepuis && (
              <div className="mb-4 bg-red-500 bg-opacity-90 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-5 w-5" />
                  <span className="font-medium text-sm">
                    Accès bloqué depuis le {new Date(balance.accesBloqueDepuis).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <p className="text-sm mt-1">
                  Toute dette doit être réglée avant la fin de la miellée. Régularisez votre solde
                  pour rétablir l&apos;accès et pouvoir à nouveau réserver. Les réservations en cours
                  ont été annulées.
                </p>
              </div>
            )}

            {dette > 0 && (
              <button
                type="button"
                onClick={() => setShowPaymentModal(true)}
                className="w-full px-4 py-3 bg-white text-honey-700 rounded-lg hover:bg-honey-50 font-semibold transition-colors flex items-center justify-center gap-2 shadow-sm"
              >
                <CreditCardIcon className="h-5 w-5" />
                Régulariser ma dette
              </button>
            )}
          </div>
        </div>

        {/* Colonne 2/3 — historique */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Historique des transactions
            </h3>
            <span className="text-sm text-slate-500">
              {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
            </span>
          </div>

          {transactions.length === 0 ? (
            <div className="p-12 text-center">
              <BanknotesIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune transaction</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-200">
                {paginatedTransactions.map((transaction) => (
                  <div key={transaction._id} className="p-5 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                          {getTransactionIcon(transaction)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h4 className="font-medium text-slate-900 truncate">
                              {formatTransactionDescription(transaction.description)}
                            </h4>
                          </div>

                          <div className="text-sm text-slate-600 space-y-1">

                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`pl-2 pr-2.5 py-0.5 rounded text-xs font-medium ${
                                estAvoir(transaction) || transaction.type === 'annulation' ? 'bg-blue-100 text-blue-800' :
                                transaction.type === 'paiement' ? 'bg-green-100 text-green-800' :
                                transaction.type === 'reservation' ? 'bg-red-100 text-red-800' :
                                'bg-honey-100 text-honey-800'
                              }`}>
                                {estAvoir(transaction) && transaction.type !== 'annulation'
                                  ? 'Avoir'
                                  : transactionTypeLabel(transaction.type)}
                              </span>
                              {transaction.paiement?.methode && (
                                <span className="text-xs bg-slate-200 text-slate-900 px-2 py-0.5 rounded">
                                  {paymentMethodLabel(transaction.paiement.methode)}
                                </span>
                              )}
                              {transaction.paiement?.reference && (
                                <span className="text-xs text-slate-500">
                                  Réf: {transaction.paiement.reference}
                                </span>
                              )}
                            </div>

                            <div className="text-xs text-slate-500 pt-0.5">
                              {transaction.effectuePar
                                ? `Validé par ${transaction.effectuePar.prenom} ${transaction.effectuePar.nom} le ${formatTransactionQuand(transaction.createdAt)}`
                                : transaction.paiement?.methode === 'stripe'
                                  ? `Payé en ligne le ${formatTransactionQuand(transaction.createdAt)}`
                                  : `Enregistré le ${formatTransactionQuand(transaction.createdAt)}`}
                            </div>
                            {transaction.responsableCreneau && (
                              <div className="text-xs text-slate-500">
                                Responsable du créneau : {transaction.responsableCreneau.prenom}{' '}
                                {transaction.responsableCreneau.nom}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className={`text-lg font-semibold ${getTransactionColor(transaction)}`}>
                          {transaction.montant > 0 && !estPrepayementEnLigne(transaction) ? '+' : ''}
                          {transaction.montant.toFixed(2)} €
                        </div>
                        <div className="text-sm text-slate-500">
                          Solde: {transaction.soldeApres.toFixed(2)} €
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    Page {page} sur {totalPages}
                    <span className="text-slate-400 ml-2">
                      ({(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, transactions.length)})
                    </span>
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Page précédente"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Page suivante"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showPaymentModal && dette > 0 && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <CreditCardIcon className="h-6 w-6 text-honey-600" />
                  Régulariser ma dette
                </h3>
                <button
                  type="button"
                  onClick={closePaymentModal}
                  disabled={processingPayment}
                  className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-slate-600 mb-4">
                  Dette actuelle : <span className="font-semibold text-slate-900">{dette.toFixed(2)} €</span>
                </p>
                <RegulariserDetteForm
                  dette={dette}
                  montantPaiement={montantPaiement}
                  setMontantPaiement={setMontantPaiement}
                  processingPayment={processingPayment}
                  onSubmit={handlePayment}
                />
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
